import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Model as SurveyModel } from 'survey-core';

import { BeneficiaryService } from '@core/services/use-cases/beneficiary.service';
import { BeneficiaryResponseEntry } from '@core/services/use-cases/form.service';
import { TableColumn } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-column';

/**
 * Construye el contenido del Excel de exportación de respuestas de formularios (JRS-036).
 *
 * El fichero combina dos bloques de columnas:
 *  - Izquierda: datos del beneficiario (los campos elegidos en el diálogo selector;
 *    los sensibles van desmarcados por defecto vía `DialogExportColumnsService`).
 *  - Derecha: una columna por pregunta del cuestionario, con la respuesta en texto
 *    legible (`SurveyJS displayValue`, igual que el dashboard de JRS-037).
 *
 * Las filas se devuelven ya preparadas para `ExcelExportService.exportRowsWithWorker`.
 * SurveyJS corre aquí (hilo principal); solo la escritura del XLSX se delega al worker.
 */
@Injectable({ providedIn: 'root' })
export class FormResponsesExportService {
  private readonly translate = inject(TranslateService);
  private readonly beneficiaryService = inject(BeneficiaryService);

  /**
   * Columnas de beneficiario seleccionables en el diálogo de exportación.
   * Replica las claves i18n y los flags `sensitive` de `beneficiaries-list`.
   */
  getBeneficiaryExportColumns(): TableColumn[] {
    return [
      {
        key: 'firstName',
        title: 'First Name',
        titleKey: 'beneficiaries.list.table.columns.firstName',
        type: 'text',
        sensitive: true,
      },
      {
        key: 'lastName',
        title: 'Last Name',
        titleKey: 'beneficiaries.list.table.columns.lastName',
        type: 'text',
        sensitive: true,
      },
      {
        key: 'email',
        title: 'Email',
        titleKey: 'beneficiaries.list.table.columns.email',
        type: 'email',
        sensitive: true,
      },
      {
        key: 'phone',
        title: 'Phone',
        titleKey: 'beneficiaries.list.table.columns.phone',
        type: 'phone',
        sensitive: true,
      },
      {
        key: 'gender',
        title: 'Gender',
        titleKey: 'beneficiaries.list.table.columns.gender',
        type: 'text',
      },
      {
        key: 'birthDate',
        title: 'Date of Birth',
        titleKey: 'beneficiaries.list.table.columns.birthDate',
        type: 'text',
        sensitive: true,
      },
      {
        key: 'hostCountry',
        title: 'Host Country',
        titleKey: 'beneficiaries.list.table.columns.hostCountry',
        type: 'text',
      },
      {
        key: 'birthCountry',
        title: 'Country of Birth',
        titleKey: 'beneficiaries.list.table.columns.countryOfBirth',
        type: 'text',
        sensitive: true,
      },
      {
        key: 'externalId',
        title: 'External ID',
        titleKey: 'beneficiaries.list.table.columns.externalId',
        type: 'text',
        excludeFromDefaultExport: true,
      },
    ];
  }

  /**
   * Construye filas y cabeceras del Excel a partir del schema del form y las respuestas.
   *
   * @param schema - Schema SurveyJS del formulario.
   * @param entries - Respuestas SUBMITTED con su beneficiario.
   * @param selectedBeneficiaryKeys - Claves de columnas de beneficiario elegidas en el diálogo.
   */
  buildSheet(
    schema: Record<string, unknown>,
    entries: BeneficiaryResponseEntry[],
    selectedBeneficiaryKeys: string[],
  ): { rows: Record<string, unknown>[]; headers: string[] } {
    // Columnas de beneficiario en su orden canónico, filtradas por lo seleccionado.
    const beneficiaryColumns = this.getBeneficiaryExportColumns().filter((c) =>
      selectedBeneficiaryKeys.includes(c.key),
    );

    const survey = new SurveyModel(schema ?? {});
    // Exports siempre en inglés, independientemente del locale con el que
    // el beneficiario respondió o del locale global activo en el navegador.
    survey.locale = 'en';
    const questions = survey.getAllQuestions();

    // Cabeceras crudas en orden: beneficiario primero, luego preguntas.
    const rawHeaders: string[] = [
      ...beneficiaryColumns.map((c) => (c.titleKey ? this.translate.instant(c.titleKey) : c.title)),
      ...questions.map((q) => q.title || q.name),
    ];
    const headers = this.dedupeHeaders(rawHeaders);

    const beneficiaryCount = beneficiaryColumns.length;

    const rows = entries.map((entry) => {
      const t = this.beneficiaryService.transformBeneficiaryData(
        entry.beneficiary,
      ) as unknown as Record<string, unknown>;
      if (t['gender']) {
        t['gender'] = this.translate.instant('beneficiaries.form.gender.options.' + t['gender']);
      }
      survey.data = entry.answers ?? {};

      const row: Record<string, unknown> = {};
      headers.forEach((header, i) => {
        if (i < beneficiaryCount) {
          row[header] = this.toCell(t[beneficiaryColumns[i].key]);
        } else {
          const question = questions[i - beneficiaryCount];
          row[header] = this.toCell(this.readDisplayValue(question));
        }
      });
      return row;
    });

    return { rows, headers };
  }

  /** Lee `displayValue` de una pregunta de forma defensiva. */
  private readDisplayValue(question: { displayValue?: unknown }): unknown {
    try {
      return question.displayValue;
    } catch {
      return '';
    }
  }

  /** Normaliza un valor de celda a algo que `xlsx` pueda volcar. */
  private toCell(value: unknown): string | number | boolean {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  /**
   * Evita colisiones de cabeceras repetidas (dos preguntas con el mismo título)
   * añadiendo un sufijo ` (2)`, ` (3)`… ya que `json_to_sheet` usa la cabecera como clave.
   */
  private dedupeHeaders(rawHeaders: string[]): string[] {
    const counts = new Map<string, number>();
    return rawHeaders.map((header) => {
      const seen = counts.get(header) ?? 0;
      counts.set(header, seen + 1);
      return seen === 0 ? header : `${header} (${seen + 1})`;
    });
  }
}
